import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { listTransactions } from '../lib/api';
import { useActiveBranch } from '../lib/use-branch';
import { posStatusVariant } from '../lib/pos-status';
import { salesOrderSeries } from '../lib/sales-order';
import { BranchPicker } from '../components/branch-picker';
import { TransactionDetailDialog } from '../components/transaction-detail-dialog';

const STATUSES = ['', 'completed', 'refunded', 'cancelled', 'to_release'] as const;

/**
 * Transaction History (feature 12) — branch-scoped POS order history with
 * date / status / order-code filters.
 */
export function TransactionsPage() {
  const { branchId, setSelectedBranchId, isScoped, branches } = useActiveBranch();
  // The list is branch-scoped, so every row belongs to the active branch —
  // its prefix is what we strip to show the bare "Sales Order #" series.
  const orderPrefix = branches.find((b) => b.id === branchId)?.orderPrefix ?? null;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['pos-transactions', branchId, status, q, from, to],
    enabled: !!branchId,
    queryFn: () =>
      listTransactions({
        branchId: branchId!,
        status: status || undefined,
        q: q || undefined,
        from: from ? new Date(from).toISOString() : undefined,
        to: to ? new Date(`${to}T23:59:59`).toISOString() : undefined,
        limit: 200,
      }),
  });

  return (
    <>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Sales Orders</CardTitle>
        <BranchPicker branches={branches} branchId={branchId} isScoped={isScoped} onChange={setSelectedBranchId} />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Order code</label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="0001" className="w-36" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <select
              className="h-10 rounded-md border border-input bg-background px-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s === '' ? 'All' : s}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setStatus(''); setQ(''); setFrom(''); setTo(''); }}
            disabled={!status && !q && !from && !to}
          >
            Clear
          </Button>
        </div>

        {query.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : !query.data?.length ? (
          <p className="text-sm text-muted-foreground">No sales orders match.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground border-b">
              <tr>
                <th className="px-2 py-1.5">When</th>
                <th className="px-2 py-1.5">Order</th>
                <th className="px-2 py-1.5">Status</th>
                <th className="px-2 py-1.5">Payment</th>
                <th className="px-2 py-1.5 text-right">Items</th>
                <th className="px-2 py-1.5 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {query.data.map((t) => (
                <tr
                  key={t.orderId}
                  onClick={() => setSelected(t.orderId)}
                  className="cursor-pointer border-b last:border-0 hover:bg-muted/30"
                >
                  <td className="px-2 py-1.5 text-xs text-muted-foreground">
                    {new Date(t.createdAt).toLocaleString()}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    #{salesOrderSeries(t.code, orderPrefix)}
                    {/* Marks a sale that carries a remark, so a supervisor can
                        spot one to review without opening every row. */}
                    {t.hasNotes && (
                      <span className="ml-1 text-muted-foreground" title="Has remarks">
                        ✎
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5">
                    <Badge variant={posStatusVariant(t.status)}>{t.status}</Badge>
                  </td>
                  <td className="px-2 py-1.5 text-xs">{t.paymentMethod ?? '—'}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{t.itemCount}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">
                    {t.paymentCurrency === 'cash' ? '₱' : ''}
                    {Number(t.total).toFixed(2)} {t.paymentCurrency !== 'cash' ? t.paymentCurrency.toUpperCase() : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
    <TransactionDetailDialog orderId={selected} orderPrefix={orderPrefix} onClose={() => setSelected(null)} />
    </>
  );
}
